"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Globe, ArrowRight, Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

interface LanguageStepProps {
  onNext: () => void;
  onBack?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const languages = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "🇺🇸",
    description: "Continue in English",
  },
  {
    code: "es-MX",
    name: "Spanish",
    nativeName: "Español",
    flag: "🇲🇽",
    description: "Continuar en Español",
  },
];

export function LanguageStep({ onNext }: LanguageStepProps) {
  const { toast } = useToast();
  const router = useRouter();
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectAndContinue = async (locale: string) => {
    setSelectedLocale(locale);
    setIsLoading(true);

    try {
      const response = await fetch("/api/user/locale", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale }),
      });

      if (!response.ok) {
        throw new Error("Failed to save language preference");
      }

      // Also set cookie directly for immediate effect
      document.cookie = `NEXT_LOCALE=${locale};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;

      const langName = languages.find((l) => l.code === locale)?.nativeName || locale;
      toast({
        title: locale === "es-MX" ? "¡Idioma actualizado!" : "Language updated!",
        description:
          locale === "es-MX"
            ? `JobWright ahora está en ${langName}`
            : `JobWright is now in ${langName}`,
      });

      // Refresh the page to apply locale change, then proceed
      router.refresh();

      // Small delay to let the refresh take effect
      setTimeout(() => {
        onNext();
      }, 300);
    } catch (error) {
      console.error("Error saving language:", error);
      toast({
        title: "Error",
        description: "Failed to save language preference. Please try again.",
        variant: "destructive",
      });
      setSelectedLocale(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-8 py-8">
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500">
        <Globe className="h-12 w-12 text-white" />
      </div>

      <div className="space-y-3 text-center max-w-2xl">
        <h1 className="text-4xl font-bold tracking-tight">
          Choose Your Language
        </h1>
        <p className="text-lg text-muted-foreground">
          Elige tu idioma preferido
        </p>
        <p className="text-sm text-muted-foreground">
          You can change this anytime in Settings · Puedes cambiar esto en cualquier momento
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg w-full">
        {languages.map((lang) => {
          const isSelected = selectedLocale === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => handleSelectAndContinue(lang.code)}
              disabled={isLoading}
              className={cn(
                "relative group text-left p-0 border-0 bg-transparent cursor-pointer disabled:cursor-wait",
              )}
            >
              <Card
                className={cn(
                  "transition-all duration-200 hover:shadow-lg hover:border-[#05A0D1]",
                  isSelected && "border-[#05A0D1] ring-2 ring-[#05A0D1]/20 shadow-lg",
                  isLoading && !isSelected && "opacity-50"
                )}
              >
                <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                  <span className="text-5xl">{lang.flag}</span>
                  <div>
                    <p className="text-xl font-bold">{lang.nativeName}</p>
                    {lang.name !== lang.nativeName && (
                      <p className="text-sm text-muted-foreground">{lang.name}</p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {lang.description}
                  </p>
                  {isSelected && isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#05A0D1]" />
                  ) : isSelected ? (
                    <Check className="h-5 w-5 text-[#05A0D1]" />
                  ) : (
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-[#05A0D1] transition-colors" />
                  )}
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
